
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  InputAdornment,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Groups as GroupsIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  AccountBalance as GstIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { request } from '../../../../../utils/request';
import { toast } from 'react-toastify';
import Brandcrumb from '../../../../../componentsUiBlock/Brandcrumb';
import { Party } from '../../../types';

const phoneLabels = ['mobile', 'work', 'home', 'other'];
const entityCategoryOptions = ["PARTY", "WALK_IN_CUSTOMER", "REGULAR_CUSTOMER","WEBSITE"];

const AddEditParty = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // For edit mode
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [newPhone, setNewPhone] = useState({ label: 'mobile', phoneNo: '' });
  const [newEmail, setNewEmail] = useState('');
  const [prefilledName, setPrefilledName] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm<Party>({
    defaultValues: {
      partyName: '',
      nickName: '',
      entityCategory: 'PARTY',
      enquiryStatus: 'PENDING',
      enquiry: '',
      description: '',
      assigningEmployeeId: '',
      withGST: false,
      gstNumber: '',
      contact: {
        phone: [],
        email: [],
      },
      location: '',
      address: {
        line1: '',
        city: '',
        state: '',
        country: '',
        pinCode: '',
      },
      status: 'active',
    },
    mode: 'onChange',
  });

  const formValues = watch();
  const withGST = watch('withGST');
  const gstNumber = watch('gstNumber');
  const entityCategory = watch('entityCategory');

  // Get party name from URL query parameter for quick add
  useEffect(() => {
    const partyNameFromUrl = searchParams.get('name');
    if (partyNameFromUrl && !isEditMode) {
      const decodedName = decodeURIComponent(partyNameFromUrl);
      setPrefilledName(decodedName);
      setValue('partyName', decodedName, { shouldValidate: true });

      // Auto-generate nickName if empty
      if (!formValues.nickName) {
        const shortName = decodedName.split(' ')[0] || decodedName.substring(0, 15);
        setValue('nickName', shortName, { shouldValidate: true });
      }
    }
  }, [searchParams, setValue, formValues.nickName, isEditMode]);

  // Check if we're in edit mode and fetch party data
  useEffect(() => {
    if (!id) return;
    setIsEditMode(true);

    const fetchPartyData = async () => {
      try {
        setLoading(true);
        const response = await request({
          url: `party/get-party/${id}`,
          method: 'get',
        });

        if (response.party) {
          const party = response.party;
          reset({
            partyName: party.partyName || '',
            nickName: party.nickName || '',
            entityCategory: party.entityCategory || 'PARTY',
            enquiryStatus: party.enquiryStatus || 'PENDING',
            enquiry: party.enquiry || '',
            description: party.description || '',
            assigningEmployeeId: party.assigningEmployeeId || '',
            withGST: party.withGST || false,
            gstNumber: party.gstNumber || '',
            contact: party.contact || { phone: [], email: [] },
            location: party.location || '',
            address: party.address || {
              line1: '',
              city: '',
              state: '',
              country: '',
              pinCode: '',
            },
            status: party.status || 'active',
          });
        }
      } catch (error: any) {
        console.error('Failed to fetch party:', error);
        toast.error(error || 'Failed to load party data');
      } finally {
        setLoading(false);
      }
    };

    fetchPartyData();
  }, [id, reset]);

  const onSubmit = async (data: Party) => {
    try {
      setLoading(true);

      // Clean GST number if withGST is false
      if (!data.withGST) {
        data.gstNumber = '';
      }

      // Clean enquiry fields if entityCategory is not WALK_IN_CUSTOMER
      if (data.entityCategory !== 'WALK_IN_CUSTOMER') {
        data.enquiry = '';
        data.enquiryStatus = '';
        data.description = '';
        data.assigningEmployeeId = '';
      }

      const url = isEditMode
        ? `party/update-party/${id}`
        : 'party/add-party';

      const method = isEditMode ? 'put' : 'post';

      const response = await request({
        url,
        method,
        data: data,
      });

      if (response.status === 'success') {
        toast.success(`Party ${isEditMode ? 'updated' : 'created'} successfully!`);
        isEditMode ? navigate(-1) : navigate('/parties');
      }
    } catch (error: any) {
      toast.error(error || `Failed to ${isEditMode ? 'update' : 'create'} party`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPhone = () => {
    if (newPhone.phoneNo && /^[6-9]\d{9}$/.test(newPhone.phoneNo)) {
      const updatedPhones = [...formValues.contact.phone, { ...newPhone }];
      setValue('contact.phone', updatedPhones, { shouldValidate: true });
      setNewPhone({ label: 'mobile', phoneNo: '' });
    }
  };

  const handleRemovePhone = (index: number) => {
    const updatedPhones = formValues.contact.phone.filter((_, i) => i !== index);
    setValue('contact.phone', updatedPhones, { shouldValidate: true });
  };

  const handleAddEmail = () => {
    if (newEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      const updatedEmails = [...formValues.contact.email, newEmail.toLowerCase()];
      setValue('contact.email', updatedEmails, { shouldValidate: true });
      setNewEmail('');
    }
  };

  const handleRemoveEmail = (index: number) => {
    const updatedEmails = formValues.contact.email.filter((_, i) => i !== index);
    setValue('contact.email', updatedEmails, { shouldValidate: true });
  };

  const handlePhoneLabelChange = (label: string) => {
    setNewPhone(prev => ({ ...prev, label }));
  };

  const handlePhoneNumberChange = (phoneNo: string) => {
    setNewPhone(prev => ({ ...prev, phoneNo: phoneNo.replace(/\D/g, '') }));
  };

  const handleWithGSTChange = (value: boolean) => {
    setValue('withGST', value, { shouldValidate: true });
    // Clear GST number when switching to without GST
    if (!value) {
      setValue('gstNumber', '', { shouldValidate: true });
    }
  };

  const handleEntityCategoryChange = (value: string) => {
    setValue('entityCategory', value as "PARTY" | "WALK_IN_CUSTOMER" | "REGULAR_CUSTOMER" | "WEBSITE", { shouldValidate: true });

    // Clear enquiry fields when switching from WALK_IN_CUSTOMER to other types
    if (value !== 'WALK_IN_CUSTOMER') {
      setValue('enquiry', '', { shouldValidate: true });
      setValue('enquiryStatus', '', { shouldValidate: true });
      setValue('description', '', { shouldValidate: true });
      setValue('assigningEmployeeId', '', { shouldValidate: true });
    }
  };

  // Validate GST number
  const validateGSTNumber = (value?: string) => {
    if (withGST && value && !/^[0-9A-Z]{15}$/.test(value)) {
      return 'GST number must be 15 characters alphanumeric';
    }
    return true;
  };

  const isFormValid = isValid &&
    formValues.partyName.trim().length > 0 &&
    formValues.location.trim().length > 0 &&
    (!withGST || (withGST && (gstNumber === '' || /^[0-9A-Z]{15}$/.test(gstNumber as string))));

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
        <Brandcrumb />
      </Box>
      <Box sx={{ margin: '0 auto' }}>
        {/* Header */}
        <Card elevation={3} sx={{ mb: 2 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box display="flex" alignItems="center" gap={2}>
              <GroupsIcon color="primary" sx={{ fontSize: 24 }} />
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {isEditMode ? 'Edit Party' : 'Add New Party'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {prefilledName && !isEditMode ? (
                    <>Creating party: <strong>"{prefilledName}"</strong></>
                  ) : (
                    `${isEditMode ? 'Update' : 'Create'} party profile with complete details`
                  )}
                </Typography>
              </Box>
            </Box>
            {prefilledName && !isEditMode && (
              <Chip
                label="Name prefilled from upload"
                color="info"
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
              />
            )}
          </CardContent>
        </Card>

        <Card elevation={3}>
          <CardContent sx={{ p: 3 }}>
            {loading && isEditMode ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress />
              </Box>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* Basic Information */}
                <Box mb={0}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                    Basic Information
                  </Typography>

                  <Box display="flex" gap={2} mb={2}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Party Name *"
                      {...register('partyName', {
                        required: 'Party name is required',
                        minLength: { value: 1, message: 'Party name is required' }
                      })}
                      error={!!errors.partyName}
                      helperText={errors.partyName?.message}
                      placeholder="Company or individual name"
                      InputProps={{
                        readOnly: !!prefilledName && !isEditMode,
                      }}
                      sx={{
                        '& .MuiInputBase-input.Mui-readOnly': {
                          backgroundColor: prefilledName ? 'action.hover' : 'transparent',
                          fontWeight: prefilledName ? 600 : 'normal',
                        }
                      }}
                    />
                    <TextField
                      size="small"
                      fullWidth
                      label="Nick Name"
                      {...register('nickName')}
                      placeholder="Short name or alias"
                      helperText="Optional short name for quick reference"
                    />
                  </Box>

                  {/* Entity Category Type Selection */}
                  <Box mb={2}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend" sx={{ fontSize: "14px" }}>
                        Entity Category Type *
                      </FormLabel>
                      <RadioGroup
                        row
                        value={entityCategory}
                        onChange={(e) => handleEntityCategoryChange(e.target.value)}
                      >
                        {entityCategoryOptions.map((option) => (
                          <FormControlLabel
                            key={option}
                            value={option}
                            control={<Radio size="small" />}
                            label={option.replace('_', ' ')}
                          />
                        ))}
                      </RadioGroup>
                      <FormHelperText>
                        Select the type of customer
                      </FormHelperText>
                    </FormControl>
                  </Box>

                </Box>

                {/* Contact Information */}
                <Box mb={0}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                    Contact Information
                  </Typography>


                  <Box display="flex" gap={2} mb={2}>
                    {/* Phone Numbers */}
                    <Box flex={1}>
                      <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                        Phone Numbers *
                      </Typography>
                      <Box display="flex" gap={1} mb={1}>
                        <TextField
                          select
                          size="small"
                          value={newPhone.label}
                          onChange={(e) => handlePhoneLabelChange(e.target.value)}
                          sx={{ minWidth: 100 }}
                        >
                          {phoneLabels.map((label) => (
                            <MenuItem key={label} value={label}>
                              {label}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          size="small"
                          value={newPhone.phoneNo}
                          onChange={(e) => handlePhoneNumberChange(e.target.value)}
                          placeholder="10-digit number"
                          error={!!newPhone.phoneNo && !/^[6-9]\d{9}$/.test(newPhone.phoneNo)}
                          helperText={!!newPhone.phoneNo && !/^[6-9]\d{9}$/.test(newPhone.phoneNo) ? 'Invalid phone number' : ''}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPhone())}
                          fullWidth
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIcon fontSize="small" color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleAddPhone}
                          disabled={!newPhone.phoneNo || !/^[6-9]\d{9}$/.test(newPhone.phoneNo)}
                          sx={{ minWidth: 'auto', px: 1 }}
                        >
                          <AddIcon fontSize="small" />
                        </Button>
                      </Box>
                      <Box display="flex" flexWrap="wrap" gap={0.5} minHeight={32}>
                        {formValues.contact.phone.map((phone, index) => (
                          <Chip
                            key={index}
                            label={`${phone.label}: ${phone.phoneNo}`}
                            size="small"
                            onDelete={() => handleRemovePhone(index)}
                            color="primary"
                            variant="filled"
                          />
                        ))}
                      </Box>
                      {/* <FormHelperText error={formValues.contact.phone.length === 0}>
                        {formValues.contact.phone.length === 0 && 'Add at least one phone number'}
                      </FormHelperText> */}
                    </Box>

                    {/* Email Addresses */}
                    <Box flex={1}>
                      <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                        Email Addresses
                      </Typography>
                      <Box display="flex" gap={1} mb={1}>
                        <TextField
                          size="small"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          placeholder="email@domain.com"
                          error={!!newEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)}
                          helperText={!!newEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail) ? 'Invalid email' : ''}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddEmail())}
                          fullWidth
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailIcon fontSize="small" color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleAddEmail}
                          disabled={!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)}
                          sx={{ minWidth: 'auto', px: 1 }}
                        >
                          <AddIcon fontSize="small" />
                        </Button>
                      </Box>
                      <Box display="flex" flexWrap="wrap" gap={0.5} minHeight={32}>
                        {formValues.contact.email.map((email, index) => (
                          <Chip
                            key={index}
                            label={email}
                            size="small"
                            onDelete={() => handleRemoveEmail(index)}
                            color="secondary"
                            variant="filled"
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* GST Information - ADDED SECTION */}
                <Box mb={0}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                    GST Information
                  </Typography>

                  <Box display="flex" flexDirection="column" gap={2} mb={2}>
                    {/* GST Selection Radio Buttons */}
                    <FormControl component="fieldset">
                      <FormLabel component="legend" sx={{ fontSize: "14px" }}>GST Type *</FormLabel>
                      <RadioGroup
                        row
                        value={withGST ? 'true' : 'false'}
                        onChange={(e) => handleWithGSTChange(e.target.value === 'true')}
                      >
                        <FormControlLabel
                          value="false"
                          control={<Radio size="small" />}
                          label="Without GST"
                        />
                        <FormControlLabel
                          value="true"
                          control={<Radio size="small" />}
                          label="With GST"
                        />
                      </RadioGroup>
                      <FormHelperText>
                        Select whether this party requires GST billing
                      </FormHelperText>
                    </FormControl>

                    {withGST && (
                      <Box display="flex" gap={2}>
                        <Controller
                          name="gstNumber"
                          control={control}
                          rules={{ validate: validateGSTNumber }}
                          render={({ field }) => (
                            <TextField
                              size="small"
                              fullWidth
                              label="GST Number"
                              {...field}
                              value={field.value || ''}
                              onChange={(e) => {
                                const cleaned = e.target.value.toUpperCase().replace(/\s/g, '');
                                field.onChange(cleaned);
                              }}
                              placeholder="15-digit GSTIN (e.g., 27ABCDE1234F1Z5)"
                              error={!!errors.gstNumber}
                              helperText={
                                errors.gstNumber?.message ||
                                (field.value && !/^[0-9A-Z]{15}$/.test(field.value)
                                  ? 'GST number must be 15 characters alphanumeric'
                                  : 'Enter 15-digit GSTIN (optional)')
                              }
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <GstIcon fontSize="small" color="action" />
                                  </InputAdornment>
                                ),
                                inputProps: {
                                  maxLength: 15,
                                  style: { textTransform: 'uppercase' }
                                }
                              }}
                            />
                          )}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Location & Address */}
                <Box mb={0}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                    Location & Address
                  </Typography>


                  <Box display="flex" gap={2} mb={2}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Location *"
                      {...register('location', {
                        required: 'Location is required',
                        minLength: { value: 1, message: 'Location is required' }
                      })}
                      error={!!errors.location}
                      helperText={errors.location?.message}
                      placeholder="Business location or area"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Box display="flex" gap={2} mb={2}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Address Line 1"
                      {...register('address.line1')}
                      placeholder="Street address, building name"
                    />
                  </Box>

                  <Box display="flex" gap={2} mb={2}>
                    <TextField
                      size="small"
                      fullWidth
                      label="City"
                      {...register('address.city')}
                      placeholder="City"
                    />
                    <TextField
                      size="small"
                      fullWidth
                      label="State"
                      {...register('address.state')}
                      placeholder="State"
                    />
                    <TextField
                      size="small"
                      fullWidth
                      label="PIN Code"
                      {...register('address.pinCode')}
                      placeholder="PIN Code"
                    />

                    <TextField
                      size="small"
                      fullWidth
                      label="Country"
                      {...register('address.country')}
                      placeholder="Country"
                    />
                  </Box>
                </Box>

                {/* Status & Submit */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      {...register('status')}
                      defaultValue="active"
                      label="Status"
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>

                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={isEditMode ? () => navigate(-1) : () => navigate('/parties')}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      type="submit"
                      variant="contained"
                      disabled={loading || !isFormValid}
                      startIcon={loading ? <CircularProgress size={16} /> : null}
                    >
                      {loading
                        ? (isEditMode ? 'Updating...' : 'Creating...')
                        : (isEditMode ? 'Update Party' : 'Create Party')
                      }
                    </Button>
                  </Box>
                </Box>
              </form>
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default AddEditParty;